/// <reference path="./external.d.ts" />
import {data} from './data';
import {logging} from './logging';

export namespace util {
  /**
   * Delay for running expensive tasks, in milliseconds.
   * The duration was empirically found so that it leaves enough time for the
   * browser to update its UI state before starting an expensive UI-blocking task.
   */
  const TASK_DELAY_MS = 200;

  /** Shuffles the array in-place in O(n) time using Fisher-Yates algorithm. */
  export function shuffle<T>(array: T[]): T[] {
    let m = array.length;
    let t: T;
    let i: number;

    // While there remain elements to shuffle.
    while (m) {
      // Pick a remaining element
      i = Math.floor(Math.random() * m--);
      // And swap it with the current element.
      t = array[m];
      array[m] = array[i];
      array[i] = t;
    }
    return array;
  }

  export function range(count: number): number[] {
    const rangeOutput: number[] = [];
    for (let i = 0; i < count; i++) {
      rangeOutput.push(i);
    }
    return rangeOutput;
  }

  export function classed(element: HTMLElement, className: string, enabled: boolean) {
    const classNames = element.className.split(' ');
    if (enabled) {
      if (className in classNames) {
        return;
      } else {
        classNames.push(className);
      }
    } else {
      const index = classNames.indexOf(className);
      if (index === -1) {
        return;
      }
      classNames.splice(index, 1);
    }
    element.className = classNames.join(' ');
  }

  /**
   * Assert that the condition is satisfied; if not, log user-specified message
   * to the console.
   */
  export function assert(condition: boolean, message?: string) {
    if (!condition) {
      message = message || 'Assertion failed';
      throw new Error(message);
    }
  }

  export type SearchPredicate = (p: data.DataPoint) => boolean;

  export function getSearchPredicate(
    query: string,
    inRegexMode: boolean,
    fieldName: string
  ): SearchPredicate {
    let predicate: SearchPredicate;
    if (inRegexMode) {
      let regExp = new RegExp(query, 'i');
      predicate = p => regExp.test(p.metadata[fieldName].toString());
    } else {
      // Doing a case insensitive substring match.
      query = query.toLowerCase();
      predicate = p => {
        let label = p.metadata[fieldName].toString().toLowerCase();
        return label.indexOf(query) >= 0;
      };
    }
    return predicate;
  }

  /**
   * Runs an expensive task asynchronously with some delay
   * so that it doesn't block the UI thread immediately.
   *
   * @param message The message to display to the user.
   * @param task The expensive task to run.
   * @param msgId Optional. ID of an existing message. If provided, will overwrite
   *     an existing message and won't automatically clear the message when the
   *     task is done.
   * @return The value returned by the task.
   */
  export function runAsyncTask<T>(
    message: string,
    task: () => T,
    msgId?: string
  ): Promise<T> {
    let autoClear = msgId == null;
    msgId = logging.setModalMessage(message, msgId);
    return new Promise<T>((resolve, reject) => {
      setTimeout(() => {
        try {
          let result = task();
          // Clearing the old message.
          if (autoClear) {
            logging.setModalMessage(null, msgId);
          }
          resolve(result);
        } catch (ex) {
          reject(ex);
        }
        return true;
      }, TASK_DELAY_MS);
    });
  }

  /**
   * Parses the URL for query parameters, e.g. ?foo=1&bar=2 will return
   *   {'foo': '1', 'bar': '2'}.
   * @param url The URL to parse.
   * @return A map of queryParam key to its value.
   */
  export function getURLParams(url: string): {[key: string]: string} {
    if (!url) {
      return {};
    }

    let queryString = url.indexOf('?') !== -1 ? url.split('?')[1] : url;
    if (queryString.indexOf('#')) {
      queryString = queryString.split('#')[0];
    }

    const queryEntries = queryString.split('&');
    let queryParams: {[key: string]: string} = {};
    for (let i = 0; i < queryEntries.length; i++) {
      let queryEntryComponents = queryEntries[i].split('=');
      queryParams[queryEntryComponents[0].toLowerCase()] = decodeURIComponent(
        queryEntryComponents[1]
      );
    }
    return queryParams;
  }

  /** List of substrings that auto generated tensors have in their name. */
  const SUBSTR_GEN_TENSORS = ['/Adagrad'];

  /** Returns true if the tensor was automatically generated by TF API calls. */
  export function tensorIsGenerated(tensorName: string): boolean {
    for (let i = 0; i < SUBSTR_GEN_TENSORS.length; i++) {
      if (tensorName.indexOf(SUBSTR_GEN_TENSORS[i]) >= 0) {
        return true;
      }
    }
    return false;
  }

  export function xor(cond1: boolean, cond2: boolean): boolean {
    return (cond1 || cond2) && !(cond1 && cond2);
  }

  /** Checks to see if the browser supports webgl. */
  export function hasWebGLSupport(): boolean {
    try {
      let c = document.createElement('canvas');
      let gl = c.getContext('webgl') || c.getContext('experimental-webgl');
      return gl != null && typeof weblas !== 'undefined';
    } catch (e) {
      return false;
    }
  }
}
